import dashboardIcon from '../../assets/icons/dashboard.svg';
import taskIcon from '../../assets/icons/task.svg';
import featuresIcon from '../../assets/icons/features.svg';
import pluginIcon from '../../assets/icons/plugin.svg';
import contentMasterIcon from '../../assets/icons/content-master.svg';
import shipmentIcon from '../../assets/icons/shipment.svg';
import matrixIcon from '../../assets/icons/matrix-1.svg';
import settingsIcon from '../../assets/icons/settings.svg';
import starsIcon from '../../assets/icons/stars.svg';

export type SidebarMenuEntry = {
  id: string;
  labelKey: string;
  icon: string;
  to?: string;
  isExpandable?: boolean;
  hiddenForSubuser?: boolean;
  /** Subusers must hold this permission key, otherwise the entry is hidden. */
  requiredPermission?: string;
  /** Shown only when GET /affiliate/me confirms the user is an active partner. */
  partnerOnly?: boolean;
  children?: SidebarMenuEntry[];
};

export const SIDEBAR_MENU: SidebarMenuEntry[] = [
  {
    id: 'dashboard',
    labelKey: 'sidebar_dashboard',
    icon: dashboardIcon,
    to: '/dashboard',
    requiredPermission: 'mobile_read',
  },
  {
    id: 'tasks',
    labelKey: 'sidebar_tasks',
    icon: taskIcon,
    to: '/tasks',
    requiredPermission: 'mobile_read',
  },
  {
    id: 'features',
    labelKey: 'sidebar_features',
    icon: featuresIcon,
    to: '/features',
  },
  {
    id: 'connectors',
    labelKey: 'sidebar_connectors',
    icon: pluginIcon,
    to: '/connectors',
  },
  {
    id: 'content-master',
    labelKey: 'sidebar_content_master',
    icon: contentMasterIcon,
    requiredPermission: 'content_master_read',
  },

  {
    id: 'shipment-tracker',
    labelKey: 'sidebar_shipment_tracker',
    icon: shipmentIcon,
    requiredPermission: 'shipment_tracker_read',
    isExpandable: true,
    children: [
      {
        id: 'shipment-tracker-dashboard',
        labelKey: 'sidebar_shipment_tracker_dashboard',
        icon: dashboardIcon,
        to: '/features/shipment-tracker',
      },
      {
        id: 'shipment-tracker-shipments',
        labelKey: 'sidebar_shipment_tracker_shipments',
        icon: taskIcon,
        to: '/features/shipment-tracker/shipments',
      },
      {
        id: 'shipment-tracker-status-board',
        labelKey: 'sidebar_shipment_tracker_status_board',
        icon: matrixIcon,
        to: '/features/shipment-tracker/status-board',
      },
      {
        id: 'shipment-tracker-settings',
        labelKey: 'sidebar_shipment_tracker_settings',
        icon: settingsIcon,
        to: '/features/shipment-tracker/settings',
      },
    ],
  },
  {
    id: 'partner',
    labelKey: 'sidebar_partner',
    icon: starsIcon,
    to: '/partner-dashboard',
    partnerOnly: true,
  },
];

const entryMatchesPath = (
  entry: SidebarMenuEntry,
  pathname: string,
): boolean => {
  if (!entry.to) {
    return false;
  }
  return pathname === entry.to || pathname.startsWith(`${entry.to}/`);
};

/** Returns the single most specific menu entry id that matches the current route. */
export const getActiveSidebarEntryId = (pathname: string): string | null => {
  let activeId: string | null = null;
  let longestMatch = -1;

  const visit = (entry: SidebarMenuEntry) => {
    if (entry.to && entryMatchesPath(entry, pathname)) {
      if (entry.to.length > longestMatch) {
        longestMatch = entry.to.length;
        activeId = entry.id;
      }
    }
    entry.children?.forEach(visit);
  };

  SIDEBAR_MENU.forEach(visit);
  return activeId;
};

/** Auto-expand parents when a child route is active (does not control selection). */
export const getRouteExpandedIds = (pathname: string): Set<string> => {
  const expanded = new Set<string>();

  SIDEBAR_MENU.forEach((entry) => {
    if (entry.children?.some((child) => entryMatchesPath(child, pathname))) {
      expanded.add(entry.id);
    }
  });

  return expanded;
};
